import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "../ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import {
  ActivityIcon, AlertTriangle, CheckCircle2, Info, RefreshCw, Settings, Sparkles,
  PlayCircle, FileText, MessageSquare, Search, ThumbsUp, ThumbsDown, Users,
} from "lucide-react";

/* ------------------------------------------------------------------- */
/* helpers                                                              */
/* ------------------------------------------------------------------- */
const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const fmtNum = (v) => (v == null ? "—" : Number(v).toFixed(3));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
};
const tintFor = (v) => {
  if (v == null) return "text-muted-foreground";
  if (v >= 0.85) return "text-emerald-700 dark:text-emerald-300";
  if (v >= 0.70) return "text-amber-700 dark:text-amber-300";
  return "text-red-700 dark:text-red-300";
};

/* ------------------------------------------------------------------- */
/* Card title with explanatory tooltip                                  */
/* ------------------------------------------------------------------- */
function CardTitleTip({ icon: Icon, title, count, tip, testid }) {
  return (
    <CardTitle className="text-base font-display flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4" />} {title}
      {typeof count === "number" && (
        <span className="text-xs font-mono text-muted-foreground">({count})</span>
      )}
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info
              className="h-3.5 w-3.5 text-muted-foreground cursor-help"
              data-testid={testid}
            />
          </TooltipTrigger>
          <TooltipContent className="max-w-md text-xs leading-relaxed whitespace-pre-line">
            {tip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </CardTitle>
  );
}

/* ------------------------------------------------------------------- */
/* KPI tile                                                             */
/* ------------------------------------------------------------------- */
function KpiTile({ label, value, tooltip, formatter = fmtPct, testid }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Card className="rounded-sm" data-testid={testid}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                  {label}
                </div>
                <Info className="h-3 w-3 text-muted-foreground/70" />
              </div>
              <div className={`mt-2 text-2xl font-display font-bold ${tintFor(value)}`}>
                {formatter(value)}
              </div>
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ------------------------------------------------------------------- */
/* Settings dialog                                                      */
/* ------------------------------------------------------------------- */
function SettingsDialog({ open, onOpenChange, settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings || {}); }, [settings, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        live_judge_sample_rate: Number(form.live_judge_sample_rate),
        weak_retrieval_threshold: Number(form.weak_retrieval_threshold),
        sample_per_doc: parseInt(form.sample_per_doc, 10),
        per_chunk: parseInt(form.per_chunk, 10),
        synthetic_enabled: !!form.synthetic_enabled,
        live_judge_enabled: !!form.live_judge_enabled,
      };
      const { data } = await api.put("/admin/eval/settings", body);
      toast.success("Eval settings saved");
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ label, tooltip, children, testid }) => (
    <div className="grid grid-cols-12 gap-3 items-start py-3 border-b border-border last:border-0">
      <div className="col-span-5">
        <div className="flex items-center gap-1.5">
          <Label className="text-sm">{label}</Label>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground cursor-help" data-testid={`${testid}-tooltip`} />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm text-xs leading-relaxed">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="col-span-7">{children}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="eval-settings-dialog">
        <DialogHeader>
          <DialogTitle>Auto-eval settings</DialogTitle>
          <DialogDescription>
            Tune how the quality engines sample your traffic. Defaults are sensible — bump the sample rates up on low-volume tenants to get statistically meaningful signals.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <Row
            label="Live-judge sample rate"
            testid="setting-sample-rate"
            tooltip="What fraction of real RAG queries get scored by the judge LLM (groundedness / context-relevance / answer-relevance). 0.05 = 5% sampled. Higher = more signal, higher LLM cost. Range 0.00–1.00."
          >
            <Input
              type="number" min={0} max={1} step={0.01}
              value={form.live_judge_sample_rate ?? ""}
              onChange={(e) => setField("live_judge_sample_rate", e.target.value)}
              data-testid="setting-sample-rate-input"
            />
          </Row>

          <Row
            label="Weak-retrieval threshold"
            testid="setting-threshold"
            tooltip="Cosine-similarity floor for the FREE alarm. If the top retrieved chunk's score is BELOW this number, the query is flagged as 'weak retrieval'. Lower = noisier (more flags). 0.30 is a sane default for most embedding models."
          >
            <Input
              type="number" min={0} max={1} step={0.01}
              value={form.weak_retrieval_threshold ?? ""}
              onChange={(e) => setField("weak_retrieval_threshold", e.target.value)}
              data-testid="setting-threshold-input"
            />
          </Row>

          <Row
            label="Chunks sampled per doc"
            testid="setting-per-doc"
            tooltip="How many chunks the synthetic test picks from each uploaded document. More chunks = better coverage of the doc, higher cost. Range 1–50, default 5."
          >
            <Input
              type="number" min={1} max={50}
              value={form.sample_per_doc ?? ""}
              onChange={(e) => setField("sample_per_doc", e.target.value)}
              data-testid="setting-per-doc-input"
            />
          </Row>

          <Row
            label="Questions per chunk"
            testid="setting-per-chunk"
            tooltip="How many questions the LLM generates for each sampled chunk. More questions = more test cases per doc, higher cost. Range 1–10, default 2."
          >
            <Input
              type="number" min={1} max={10}
              value={form.per_chunk ?? ""}
              onChange={(e) => setField("per_chunk", e.target.value)}
              data-testid="setting-per-chunk-input"
            />
          </Row>

          <Row
            label="Synthetic eval enabled"
            testid="setting-synth-enabled"
            tooltip="Master kill-switch for the post-upload synthetic test. When OFF, no R@K / MRR is computed for new uploads."
          >
            <Switch
              checked={!!form.synthetic_enabled}
              onCheckedChange={(v) => setField("synthetic_enabled", v)}
              data-testid="setting-synth-enabled-switch"
            />
          </Row>

          <Row
            label="Live judge enabled"
            testid="setting-judge-enabled"
            tooltip="Master kill-switch for the live judge. When OFF, real RAG answers are NOT scored regardless of the sample rate."
          >
            <Switch
              checked={!!form.live_judge_enabled}
              onCheckedChange={(v) => setField("live_judge_enabled", v)}
              data-testid="setting-judge-enabled-switch"
            />
          </Row>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="eval-settings-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="eval-settings-save">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------- */
/* Main tab                                                             */
/* ------------------------------------------------------------------- */
export default function EvalTab() {
  const [kpis, setKpis] = useState(null);
  const [synthRows, setSynthRows] = useState([]);
  const [judgeRows, setJudgeRows] = useState([]);
  const [weakRows, setWeakRows] = useState([]);
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [disagreementRows, setDisagreementRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [days, setDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const [k, s, sy, ju, we, fb, dis] = await Promise.all([
        api.get(`/admin/eval/kpis?days=${days}`),
        api.get("/admin/eval/settings"),
        api.get("/admin/eval/results?type=synthetic&limit=50"),
        api.get("/admin/eval/judge-stream?limit=50"),
        api.get("/admin/eval/weak-retrievals?limit=50"),
        api.get("/admin/eval/feedback?limit=50"),
        api.get("/admin/eval/disagreement?limit=50"),
      ]);
      setKpis(k.data);
      setSettings(s.data);
      setSynthRows(sy.data.rows || []);
      setJudgeRows(ju.data.rows || []);
      setWeakRows(we.data.rows || []);
      setFeedbackRows(fb.data.rows || []);
      setDisagreementRows(dis.data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load eval data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const runNow = async (fileId) => {
    setRunningId(fileId);
    try {
      const { data } = await api.post(`/admin/eval/run-now?file_id=${encodeURIComponent(fileId)}`);
      if (data.ok) {
        toast.success(`Re-evaluated — R@5 ${fmtPct(data.row?.recall_5)}`);
        load();
      } else {
        // B-4 fix — precise reason from backend
        toast.warning(data.message || data.reason || "Not eligible");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Run failed");
    } finally {
      setRunningId(null);
    }
  };

  const totalEvents = useMemo(() => ({
    synth: kpis?.synthetic_count ?? 0,
    judge: kpis?.live_judge_count ?? 0,
    weak: kpis?.weak_count ?? 0,
  }), [kpis]);

  return (
    <div className="space-y-8" data-testid="eval-tab">
      {/* ------------------- Header bar ----------------------------- */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">
            [ retrieval quality ]
          </div>
          <h2 className="font-display font-bold text-2xl mt-1 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Auto-eval dashboard
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Synthetic R@K + MRR per uploaded doc, live-judge sample on real RAG queries, and a free alarm for weak retrievals. All powered by your configured <code className="text-xs">auto_eval</code> operational model.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-sm border border-border p-1" data-testid="eval-window-selector">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2 py-1 text-xs rounded-sm transition-colors ${days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`eval-window-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="eval-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="eval-settings-open">
            <Settings className="h-3.5 w-3.5 mr-1.5" /> Settings
          </Button>
        </div>
      </div>

      {/* ------------------- KPI tiles ------------------------------ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        <KpiTile
          label="R@1"
          value={kpis?.recall_1}
          tooltip="Synthetic test — fraction of generated questions where the CORRECT chunk is returned as Qdrant's #1 result. Higher = better. <70% red, 70-85% amber, ≥85% green."
          testid="kpi-recall-1"
        />
        <KpiTile
          label="R@3"
          value={kpis?.recall_3}
          tooltip="Synthetic test — fraction where the correct chunk is in the top 3."
          testid="kpi-recall-3"
        />
        <KpiTile
          label="R@5"
          value={kpis?.recall_5}
          tooltip="Synthetic test — fraction where the correct chunk is in the top 5 (i.e. seen by the answering LLM)."
          testid="kpi-recall-5"
        />
        <KpiTile
          label="MRR"
          value={kpis?.mrr}
          tooltip="Mean Reciprocal Rank — average of 1/rank for each question. 1.0 = always #1, 0.5 = on average rank ~2, etc."
          formatter={fmtNum}
          testid="kpi-mrr"
        />
        <KpiTile
          label="Groundedness"
          value={kpis?.groundedness}
          tooltip="Live judge — does the answer cite the retrieved chunks correctly (no hallucinations)? Averaged over sampled queries."
          testid="kpi-groundedness"
        />
        <KpiTile
          label="User satisfaction"
          value={kpis?.user_satisfaction}
          tooltip={`Real user 👍/👎 ratings. ${kpis?.feedback_up || 0} 👍 / ${kpis?.feedback_down || 0} 👎 in the last ${days}d. Lower than groundedness? The LLM judge is missing things users care about — see the disagreement card below.`}
          testid="kpi-user-satisfaction"
        />
        <KpiTile
          label="Weak count"
          value={totalEvents.weak}
          formatter={(v) => String(v ?? 0)}
          tooltip="Free alarm — number of real queries in the window where the top chunk's similarity was below the configured threshold."
          testid="kpi-weak-count"
        />
      </div>

      {/* ------------------- Synthetic table ------------------------ */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3">
          <CardTitleTip
            icon={FileText}
            title="Synthetic eval — recent docs"
            count={synthRows.length}
            testid="tooltip-synthetic"
            tip={
              "After every file upload (PDF/DocX/text/image — and now also audio/URL/YouTube), an LLM auto-generates test questions from random chunks of YOUR document, then runs them through Qdrant to measure if the RIGHT chunk gets retrieved.\n\n" +
              "R@1 = % of questions where the correct chunk is the #1 search result\n" +
              "R@3 = % where it's in the top 3\n" +
              "R@5 = % where it's in the top 5 (i.e. seen by the answering LLM)\n" +
              "MRR = mean reciprocal rank (1.0 = always #1, 0.5 = on average rank 2, etc.)\n\n" +
              "Example: a 50-page contract with R@5=0.95 means 95% of test questions retrieve the right clause. A row showing R@5=0.45 means almost half the questions miss — likely a chunking or embedding problem for that file type.\n\n" +
              "Click 'Run now' on any row to re-evaluate (useful after changing the chunking profile or embedding model)."
            }
          />
        </CardHeader>
        <CardContent className="p-0">
          {synthRows.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="eval-synth-empty">
              No synthetic eval rows yet. Upload a file — it runs automatically post-embed (takes ~30s).
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-wide">File</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide">Profile</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide text-right">Qs</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide text-right">R@1</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide text-right">R@3</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide text-right">R@5</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide text-right">MRR</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-wide">When</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {synthRows.map((r) => (
                    <tr key={r.id} className="border-t border-border" data-testid={`eval-synth-row-${r.file_id}`}>
                      <td className="px-4 py-2 max-w-[18rem] truncate" title={r.file_name || r.file_id}>
                        {r.file_name || r.file_id}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{r.profile || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.n_questions}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${tintFor(r.recall_1)}`}>{fmtPct(r.recall_1)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${tintFor(r.recall_3)}`}>{fmtPct(r.recall_3)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${tintFor(r.recall_5)}`}>{fmtPct(r.recall_5)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${tintFor(r.mrr)}`}>{fmtNum(r.mrr)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => runNow(r.file_id)}
                          disabled={runningId === r.file_id}
                          data-testid={`eval-run-now-${r.file_id}`}
                        >
                          <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
                          {runningId === r.file_id ? "Running…" : "Run now"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------------------- Bottom split: judge + weak ------------ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live judge feed */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitleTip
              icon={MessageSquare}
              title="Live judge — recent"
              count={judgeRows.length}
              testid="tooltip-live-judge"
              tip={
                "After every RAG chat, with a configurable probability (default 5%), an LLM silently grades the AI's answer on 3 things:\n" +
                "• Groundedness — did the AI invent facts not in the document?\n" +
                "• Context relevance — were the retrieved chunks actually about the question?\n" +
                "• Answer relevance — did the AI address what the user asked?\n\n" +
                "Example: User asks \"What's the notice period?\" → AI replies \"30 days.\" → Judge sees the clause says exactly that → G=1.0 C=0.95 A=1.0 ✅\n\n" +
                "Counter-example: User asks \"What's the cancellation fee?\" → AI replies \"$50\" but no fee is in the doc → G=0.1 (invented) C=0.3 (irrelevant chunks) A=0.7 ⚠️\n\n" +
                "Empty card? Either you haven't chatted enough at the current sample rate, OR the judge LLM is failing silently. Bump sample rate to 1.0 in Settings to test."
              }
            />
          </CardHeader>
          <CardContent className="p-0">
            {judgeRows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="eval-judge-empty">
                No judged queries yet. The judge samples {settings ? `${(settings.live_judge_sample_rate * 100).toFixed(0)}%` : "~5%"} of real RAG chats.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
                {judgeRows.map((r) => (
                  <div key={r.id} className="px-4 py-3" data-testid={`eval-judge-row-${r.id}`}>
                    <div className="text-sm font-medium truncate" title={r.question}>{r.question}</div>
                    <div className="flex items-center gap-3 mt-1 text-xs font-mono">
                      <span className={tintFor(r.groundedness)}>G: {fmtNum(r.groundedness)}</span>
                      <span className={tintFor(r.context_relevance)}>C: {fmtNum(r.context_relevance)}</span>
                      <span className={tintFor(r.answer_relevance)}>A: {fmtNum(r.answer_relevance)}</span>
                      <span className="text-muted-foreground ml-auto">{fmtDate(r.created_at)}</span>
                    </div>
                    {r.reason && (
                      <div className="text-xs text-muted-foreground mt-1 italic line-clamp-2">{r.reason}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Weak retrievals */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitleTip
              icon={AlertTriangle}
              title="Weak retrievals — free alarm"
              count={weakRows.length}
              testid="tooltip-weak-retrievals"
              tip={
                "For EVERY RAG chat (100% — no sampling — costs zero LLM calls), checks the similarity score of the best chunk returned by Qdrant. If that score is below the threshold (default 0.30), the query is flagged as 'weak'.\n\n" +
                "'Weak' means: the system reached, but the closest chunk it found was a poor match for the question — likely to produce a hallucinated or off-topic answer.\n\n" +
                "Example flag: User asks \"What does the contract say about IP rights?\" but the doc has no IP section. Closest chunk found at sim=0.18. Flag fires → you know the user expected info that doesn't exist in the doc.\n\n" +
                "Healthy chat (no flag): User asks \"What's the notice period?\" → closest chunk at sim=0.78. No alarm — silence is correct.\n\n" +
                "Why is most of my traffic flagged? Likely the threshold is too high for your embedding model, OR your documents don't cover what users are asking. Lower the threshold in Settings to reduce noise, OR investigate which queries keep failing."
              }
            />
          </CardHeader>
          <CardContent className="p-0">
            {weakRows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="eval-weak-empty">
                <CheckCircle2 className="h-5 w-5 inline mr-1 text-emerald-600" />
                Nothing flagged. Top retrievals are above {settings?.weak_retrieval_threshold ?? 0.3}.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
                {weakRows.map((r) => (
                  <div key={r.id} className="px-4 py-3" data-testid={`eval-weak-row-${r.id}`}>
                    <div className="text-sm font-medium truncate" title={r.question}>{r.question}</div>
                    <div className="flex items-center gap-3 mt-1 text-xs font-mono">
                      <span className="text-red-700 dark:text-red-300">
                        top sim: {fmtNum(r.top_score)}
                      </span>
                      <span className="text-muted-foreground">
                        threshold: {fmtNum(r.threshold)}
                      </span>
                      <span className="text-muted-foreground ml-auto">{fmtDate(r.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ------------------- Phase 6.2 — User feedback + Disagreement -- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Judge ↔ User disagreement (most actionable) */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitleTip
              icon={AlertTriangle}
              title="Judge ↔ User disagreement"
              count={disagreementRows.length}
              testid="tooltip-disagreement"
              tip={
                "The MOST VALUABLE signal in the whole system. Rows here = cases where the LLM judge and the real user DISAGREED on the same AI answer.\n\n" +
                "Why it matters: if the judge says \"good\" but the user says \"bad\" (or vice versa), one of them is wrong — and the user is ground truth.\n\n" +
                "Two patterns surfaced here:\n" +
                "• Judge G≥0.7 but user 👎 → judge missed something (calibration issue, tone problem, wrong-context-but-right-format)\n" +
                "• Judge G≤0.5 but user 👍 → judge too strict (consider softening the judge prompt)\n\n" +
                "Each row is a candidate for: (1) regression test, (2) judge prompt tuning, (3) chunking/embedding investigation. Click into a row to see full context."
              }
            />
          </CardHeader>
          <CardContent className="p-0">
            {disagreementRows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="eval-disagreement-empty">
                <CheckCircle2 className="h-5 w-5 inline mr-1 text-emerald-600" />
                No disagreements detected. The LLM judge and your users are aligned.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
                {disagreementRows.map((r) => {
                  const isJudgeOverlyGood = r.rating === -1 && r.judge_g >= 0.7;
                  return (
                    <div key={r.id} className="px-4 py-3" data-testid={`eval-disagreement-row-${r.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${
                          isJudgeOverlyGood
                            ? "bg-red-100/50 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                            : "bg-amber-100/50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                        }`}>
                          {isJudgeOverlyGood ? "Judge too lenient" : "Judge too strict"}
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">{fmtDate(r.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs font-mono">
                        <span className={tintFor(r.judge_g)}>judge G: {fmtNum(r.judge_g)}</span>
                        <span className={r.rating === 1 ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}>
                          user: {r.rating === 1 ? "👍" : "👎"}
                        </span>
                      </div>
                      {r.comment && (
                        <div className="text-xs italic text-muted-foreground mt-1 line-clamp-2">"{r.comment}"</div>
                      )}
                      {r.judge_reason && (
                        <div className="text-[11px] text-muted-foreground/80 mt-1 line-clamp-1">judge: {r.judge_reason}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* User feedback feed */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitleTip
              icon={Users}
              title="User feedback — recent"
              count={feedbackRows.length}
              testid="tooltip-user-feedback"
              tip={
                "Real users clicking 👍 or 👎 on AI answers. Optional 200-char comment on 👎.\n\n" +
                "Unlike the LLM judge (which samples 5% and uses heuristics), this is ground truth. Use this feed to:\n" +
                "• Read what users hate most (look at comments)\n" +
                "• Find specific bad answers to investigate\n" +
                "• Calibrate the LLM judge by comparing to its scores (see Disagreement card)\n\n" +
                "Empty? Either no one has rated yet, OR users haven't discovered the buttons. They appear on hover over AI messages."
              }
            />
          </CardHeader>
          <CardContent className="p-0">
            {feedbackRows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="eval-feedback-empty">
                No user ratings yet. The 👍/👎 buttons appear on hover over AI messages in /workspace.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
                {feedbackRows.map((r) => (
                  <div key={r.id} className="px-4 py-3" data-testid={`eval-feedback-row-${r.id}`}>
                    <div className="flex items-center gap-2">
                      {r.rating === 1
                        ? <ThumbsUp className="h-3.5 w-3.5 text-emerald-600" />
                        : <ThumbsDown className="h-3.5 w-3.5 text-red-600" />}
                      <span className="text-xs font-mono text-muted-foreground truncate">
                        user {String(r.user_id).slice(0, 8)}
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto">{fmtDate(r.created_at)}</span>
                    </div>
                    {r.comment && (
                      <div className="text-sm mt-1 italic" data-testid={`eval-feedback-comment-${r.id}`}>
                        "{r.comment}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ------------------- Config snapshot footer ----------------- */}
      {settings && (
        <Card className="rounded-sm bg-muted/30" data-testid="eval-config-snapshot">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-2">
              [ active eval config ]
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm font-mono">
              <span>sample {`${(settings.live_judge_sample_rate * 100).toFixed(1)}%`}</span>
              <span>weak ≥ {settings.weak_retrieval_threshold}</span>
              <span>{settings.sample_per_doc} chunks × {settings.per_chunk} Qs / doc</span>
              <span className={settings.synthetic_enabled ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}>
                synthetic {settings.synthetic_enabled ? "ON" : "OFF"}
              </span>
              <span className={settings.live_judge_enabled ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}>
                live judge {settings.live_judge_enabled ? "ON" : "OFF"}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSaved={(s) => setSettings(s)}
      />
    </div>
  );
}
